import { html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { RmOldestVulnerabilitiesPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as VulnerabilitiesColumnKeys } from '../../../vulnerabilitiesApp/types';
import { State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { renderNumber, renderNumberWithTooltip, renderText } from '../../text';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { FontSizes } from '../../../../types/common/types';
import { translateFn } from '../../../../utils/translator.helper';
import { WEB_COMPONENT_GZ_RM_OLDEST_VULNERABILITIES_WIDGET } from '../../../../webcomponents.constants';
import { translateDate } from '../../../../utils/riskManagement.utils';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-oldest-vulnerabilities-widget
 */
@customElement(WEB_COMPONENT_GZ_RM_OLDEST_VULNERABILITIES_WIDGET)
export class GzRmOldestVulnerabilitiesWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmOldestVulnerabilitiesPayload = { oldestVulnerabilities: { companyId: '', total: 0, items: [] } };

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.oldestVulnerabilities?.companyId ?? '' },
      filters: [
        {
          [VulnerabilitiesColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [VulnerabilitiesColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
        // TODO: @mferaru sort by first seen date in grid
      ],
      section: 'vulnerabilities',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    const items: Row[] =
      this.payload.oldestVulnerabilities?.items?.map(
        ({ applicationName, cveCount, score, date }): RowConfig => ({
          data: { applicationName },
          cells: [
            renderText(applicationName),
            renderNumberWithTooltip(cveCount, translateFn('Total CVEs')),
            html` <gz-rm-severity .score="${score}"></gz-rm-severity>`,
            renderText(translateDate(date, 3)),
          ],
        })
      ) ?? [];

    const emptyItem = [
      renderText('...', true),
      renderText('...', true),
      html` <gz-rm-severity noHighlight></gz-rm-severity>`,
      renderText('...', true),
    ];
    const fullItems = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { applicationName: string }) => {
          if (!rowData) {
            return;
          }

          const { applicationName } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.oldestVulnerabilities?.companyId ?? '' },
            section: 'vulnerabilities',
            filters: [
              {
                [VulnerabilitiesColumnKeys.NAME]: {
                  complexEnumFilter: [{ label: applicationName, value: applicationName }],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.oldestVulnerabilities?.total ?? 0})
      </gz-link>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-oldest-vulnerabilities-widget': GzRmOldestVulnerabilitiesWidgetWebComponent;
  }
}
